/**
 * Grid-cell components migrated from designv2/src/cells.jsx.
 * Verbatim ports (1:1 with the prototype) plus TagEditor / DatePicker
 * canonical strict-typed ports from earlier slices.
 */
import {
  type CSSProperties,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { parseYmKey } from "@shared/calendar";
import type { AllocationCell, AllocationCellStored, Person, ProjectStatus } from "@shared/types";
import { allocToHours, allocToPct } from "./data";
import { parseAllocInput } from "./lib/alloc-input";
import { useYearExtras } from "./lib/holiday-cache";
import { roleTone } from "./lib/role-tones";
import { useC } from "./theme";
import { Av, Badge, SBadge, useTableStyles } from "./ui";

interface AllocCellProps {
  val: AllocationCellStored | null | undefined;
  isPlanned?: boolean;
  isTimeOff?: boolean;
  isPast?: boolean;
  lockPast?: boolean;
  isActualMode?: boolean;
  onChange: (next: AllocationCell | 0) => void;
  viewMode?: "pct" | "hours";
  personWl?: number;
  ymKey?: string;
  testId?: string;
}

export const AllocCell: FC<AllocCellProps> = ({
  val,
  isPlanned,
  isTimeOff,
  isPast,
  lockPast,
  isActualMode,
  onChange,
  viewMode,
  personWl,
  ymKey,
  testId,
}) => {
  const { C } = useC();
  const { TD } = useTableStyles();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | number>("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const showHours = viewMode === "hours";
  const wl = personWl || 100;
  const pObj = { wl } as Pick<Person, "wl">;

  const yearForExtras = (ymKey && parseYmKey(ymKey)?.y) || new Date().getUTCFullYear();
  const extras = useYearExtras(yearForExtras);
  const pctVal = val ? allocToPct(val, pObj, ymKey ?? "") : 0;
  const hrsVal = val ? allocToHours(val, pObj, ymKey ?? "", extras) : 0;

  const locked = lockPast && isPast;

  function startEdit() {
    if (locked) return;
    if (showHours) setDraft(val ? Math.round(hrsVal) : "");
    else setDraft(val ? Math.round(pctVal) : "");
    setEditing(true);
  }
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function commit() {
    const num = parseAllocInput(String(draft));
    if (num === null) {
      onChange(0);
    } else {
      onChange({ v: num, u: showHours ? "hours" : "percent" });
    }
    setEditing(false);
  }

  let bg = "transparent";
  let co = C.slateDim;
  const roundPct = Math.round(pctVal);
  if (pctVal > 0) {
    if (isTimeOff) {
      bg = C.vacBg;
      co = C.vac;
    } else if (isActualMode) {
      bg = C.blueTint1;
      co = C.navy;
    } else if (isPlanned) {
      bg = C.blueTint2;
      co = C.blueDeep;
    } else if (roundPct > 100) {
      bg = C.rBg;
      co = C.r;
    } else if (roundPct >= 80) {
      bg = C.blueTint2;
      co = C.blue;
    } else {
      co = C.slate;
    }
  }
  if (locked) {
    bg = C.lock;
    co = C.slateMut;
  }

  if (editing) {
    return (
      <td
        {...(testId ? { "data-testid": testId } : {})}
        style={{
          ...TD,
          textAlign: "center",
          background: C.blueTint2,
          padding: "3px 2px",
          minWidth: 58,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") setEditing(false);
            }}
            style={{
              width: 42,
              padding: "3px 2px",
              borderRadius: 3,
              border: `2px solid ${C.blue}`,
              fontSize: 13,
              fontWeight: 600,
              color: C.blue,
              textAlign: "center",
              outline: "none",
              background: C.surface,
              boxSizing: "border-box",
              fontFamily: "'Source Sans Pro',system-ui,sans-serif",
            }}
          />
          <span style={{ fontSize: 10, color: C.slateMut }}>{showHours ? "h" : "%"}</span>
        </div>
      </td>
    );
  }

  const hasVal = pctVal > 0;
  const displayVal = hasVal
    ? showHours
      ? `${Math.round(hrsVal)}h`
      : `${roundPct}%`
    : locked
      ? ""
      : "·";

  return (
    <td
      {...(testId ? { "data-testid": testId } : {})}
      onClick={startEdit}
      onKeyDown={(e) => {
        if (e.key === "Enter") startEdit();
      }}
      style={{
        ...TD,
        textAlign: "center",
        background: bg,
        cursor: locked ? "not-allowed" : "pointer",
        userSelect: "none",
        minWidth: 58,
        transition: "background .1s",
        opacity: locked ? 0.7 : 1,
      }}
      title={locked ? "Past month · locked" : isTimeOff ? "Time off" : ""}
      onMouseEnter={(e) => {
        if (!hasVal && !locked) e.currentTarget.style.background = C.blueTint1;
      }}
      onMouseLeave={(e) => {
        if (!hasVal && !locked) e.currentTarget.style.background = "transparent";
      }}
    >
      <span
        style={{
          fontWeight: hasVal ? 600 : 400,
          color: co,
          fontVariantNumeric: "tabular-nums",
          fontSize: 12.5,
        }}
      >
        {displayVal}
      </span>
    </td>
  );
};

/**
 * DatePicker — single unified date input used across the app.
 * Trigger shows the selected date; popover has year + month dropdowns + day grid.
 * Verbatim port from designv2/src/cells.jsx.
 *
 *   value       - "YYYY-MM-DD" (or "YYYY-MM" in monthOnly mode)
 *   dropUp      - render popover above the trigger (for modals near bottom)
 *   dropLeft    - anchor popover to the trigger's LEFT edge
 *   min         - "YYYY-MM-DD" — greys out earlier days (end-date constraint)
 *   monthOnly   - hide day grid, emit "YYYY-MM"
 *   allowClear  - month-only: render a Clear button
 */
export interface DatePickerProps {
  value: string;
  onChange: (v: string) => void;
  label?: ReactNode;
  dropUp?: boolean;
  dropLeft?: boolean;
  min?: string;
  monthOnly?: boolean;
  placeholder?: string;
  allowClear?: boolean;
  /**
   * When provided, replaces the trigger button's face text. Use for derived
   * labels (e.g. "Until Feb 2026") computed from sibling state. The popover
   * still edits `value` directly.
   */
  displayOverride?: string;
}

export const DatePicker: FC<DatePickerProps> = ({
  value,
  onChange,
  label,
  dropUp,
  dropLeft,
  min,
  monthOnly,
  placeholder,
  allowClear,
  displayOverride,
}) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthShort = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const dayHeaders = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  useEffect(() => {
    function h(e: MouseEvent) {
      const t = e.target as Node;
      if (ref.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Compute viewport-fixed popover position when opening. Portal-rendered so
  // the popover escapes any parent overflow-clip context (e.g. a modal body).
  useEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const POPOVER_W = Math.min(280, window.innerWidth - 32);
    const POPOVER_H_EST = 320;
    const VIEWPORT_MARGIN = 8;
    let left = dropLeft ? rect.left : rect.right - POPOVER_W;
    if (left + POPOVER_W > window.innerWidth - VIEWPORT_MARGIN) {
      left = window.innerWidth - POPOVER_W - VIEWPORT_MARGIN;
    }
    if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = dropUp || spaceBelow < POPOVER_H_EST;
    const top = above ? rect.top - POPOVER_H_EST - 4 : rect.bottom + 4;
    setPopoverPos({ top: Math.max(VIEWPORT_MARGIN, top), left });
  }, [open, dropLeft, dropUp]);

  const parts = (value || "").split("-").map(Number);
  const selYear = parts[0] || new Date().getFullYear();
  const selMonth = parts[1] || 1;
  const selDay = monthOnly ? 0 : parts[2] || 0;

  const [viewYear, setViewYear] = useState(selYear);
  const [viewMonth, setViewMonth] = useState(selMonth);
  useEffect(() => {
    setViewYear(selYear);
    setViewMonth(selMonth);
  }, [selYear, selMonth]);

  const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate();
  const firstDayOfWeek = (y: number, m: number) => {
    const d = new Date(y, m - 1, 1).getDay();
    return d === 0 ? 7 : d;
  };
  const totalDays = daysInMonth(viewYear, viewMonth);
  const blanks = firstDayOfWeek(viewYear, viewMonth) - 1;

  const todayY = new Date().getFullYear();
  const years: number[] = [];
  for (let y = todayY - 3; y <= todayY + 5; y++) years.push(y);

  function pickDay(d: number) {
    const ds = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    onChange(ds);
    setOpen(false);
  }
  function pickMonth() {
    onChange(`${viewYear}-${String(viewMonth).padStart(2, "0")}`);
    setOpen(false);
  }
  function clearValue() {
    onChange("");
    setOpen(false);
  }

  const hasValue = value && value.length > 0;
  const computedDisplay = monthOnly
    ? hasValue
      ? `${monthShort[selMonth - 1]} ${selYear}`
      : placeholder || "Select month"
    : selDay > 0
      ? `${selDay} ${monthShort[selMonth - 1]} ${selYear}`
      : `${monthShort[selMonth - 1]} ${selYear}`;
  const displayVal = displayOverride ?? computedDisplay;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      {label && <span style={{ fontSize: 11, color: C.slateMut, marginRight: 6 }}>{label}</span>}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          padding: "5px 12px",
          borderRadius: 6,
          border: `1px solid ${open ? C.blue : C.line}`,
          background: C.surface,
          fontSize: 12.5,
          fontWeight: hasValue ? 600 : 500,
          color: hasValue ? C.navy : C.slateMut,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          boxShadow: C.sh,
          transition: "border-color .12s",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <rect x="1" y="2" width="12" height="11" rx="2" stroke={C.slateMut} strokeWidth="1.2" />
          <path d="M1 5.5h12" stroke={C.slateMut} strokeWidth="1.2" />
          <path d="M4.5 1v2M9.5 1v2" stroke={C.slateMut} strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        {displayVal}
        <svg
          width="9"
          height="9"
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .12s" }}
        >
          <path
            d="M2.5 3.75l2.5 2.5 2.5-2.5"
            stroke={C.slateMut}
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            style={{
              position: "fixed",
              top: popoverPos.top,
              left: popoverPos.left,
              background: C.surface,
              borderRadius: 10,
              border: `1px solid ${C.line}`,
              boxShadow: C.shL,
              zIndex: 1100,
              padding: 12,
              width: "min(280px, calc(100vw - 32px))",
            }}
          >
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <select
                aria-label="Month"
                value={viewMonth}
                onChange={(e) => setViewMonth(Number(e.target.value))}
                style={{
                  flex: 1,
                  padding: "5px 8px",
                  borderRadius: 5,
                  border: `1px solid ${C.line}`,
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.navy,
                  background: C.surface,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  cursor: "pointer",
                }}
              >
                {monthNames.map((n, i) => (
                  <option key={n} value={i + 1}>
                    {n}
                  </option>
                ))}
              </select>
              <select
                aria-label="Year"
                value={viewYear}
                onChange={(e) => setViewYear(Number(e.target.value))}
                style={{
                  width: 88,
                  padding: "5px 8px",
                  borderRadius: 5,
                  border: `1px solid ${C.line}`,
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.navy,
                  background: C.surface,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  cursor: "pointer",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {!monthOnly && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  gap: 2,
                  textAlign: "center",
                }}
              >
                {dayHeaders.map((d) => (
                  <div
                    key={d}
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: C.slateMut,
                      padding: "4px 0",
                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                    }}
                  >
                    {d}
                  </div>
                ))}
                {Array.from({ length: blanks }).map((_, i) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: blank cells in calendar grid have no semantic id
                  <div key={`b${i}`} />
                ))}
                {Array.from({ length: totalDays }).map((_, i) => {
                  const day = i + 1;
                  const ds = `${viewYear}-${String(viewMonth).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isSel = ds === value;
                  const isDisabled = min ? ds < min : false;
                  return (
                    <button
                      type="button"
                      key={day}
                      onClick={() => !isDisabled && pickDay(day)}
                      disabled={isDisabled}
                      style={{
                        padding: "6px 0",
                        borderRadius: 5,
                        border: "none",
                        fontSize: 12,
                        fontWeight: isSel ? 700 : 400,
                        background: isSel ? C.blue : "transparent",
                        color: isSel ? "#fff" : isDisabled ? C.slateDim : C.slate,
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.4 : 1,
                        transition: "all .1s",
                        fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            )}
            {monthOnly && (
              <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
                {allowClear && hasValue && (
                  <button
                    type="button"
                    onClick={clearValue}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 5,
                      border: `1px solid ${C.line}`,
                      background: C.surface,
                      fontSize: 11.5,
                      fontWeight: 600,
                      color: C.slate,
                      cursor: "pointer",
                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                    }}
                  >
                    Clear
                  </button>
                )}
                <button
                  type="button"
                  onClick={pickMonth}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 5,
                    border: "none",
                    background: C.blue,
                    fontSize: 11.5,
                    fontWeight: 600,
                    color: "#fff",
                    cursor: "pointer",
                    fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  }}
                >
                  Apply
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

/* MultiSelect — verbatim port of designv2/src/cells.jsx MultiSelect.
   Used by views (Reports / Bench) for project-id multi-pick popovers. */
export interface MultiSelectOption {
  id: number;
  name: string;
  code: string;
  status: ProjectStatus;
}
export const MultiSelect: FC<{
  options: MultiSelectOption[];
  selected: number[];
  onChange: (next: number[]) => void;
  placeholder?: string;
}> = ({ options, selected, onChange, placeholder }) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    function h(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  function toggle(id: number) {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  }
  const selNames = options.filter((o) => selected.includes(o.id)).map((o) => o.name);
  const display =
    selNames.length === 0
      ? placeholder
      : selNames.length === 1
        ? selNames[0]
        : selNames.length === options.length
          ? "All projects"
          : `${selNames.length} projects`;

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          padding: "6px 12px",
          borderRadius: 6,
          border: `1px solid ${open ? C.blue : C.line}`,
          background: C.surface,
          fontSize: 12.5,
          fontWeight: 600,
          color: selNames.length ? C.navy : C.slateMut,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          boxShadow: C.sh,
          transition: "border-color .12s",
          minWidth: 200,
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      >
        <span style={{ flex: 1, textAlign: "left" }}>{display}</span>
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden="true"
          style={{
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .12s",
            flexShrink: 0,
          }}
        >
          <path
            d="M2.5 3.75l2.5 2.5 2.5-2.5"
            stroke={C.slateMut}
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            marginTop: 4,
            background: C.surface,
            borderRadius: 10,
            border: `1px solid ${C.line}`,
            boxShadow: C.shL,
            padding: 6,
            zIndex: 60,
            width: 280,
            maxHeight: 340,
            overflow: "auto",
          }}
        >
          <div style={{ display: "flex", gap: 6, marginBottom: 4, padding: "2px 4px" }}>
            <button
              type="button"
              onClick={() => onChange(options.map((o) => o.id))}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: C.blue,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => onChange([])}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: C.slateMut,
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 4,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            >
              Clear
            </button>
          </div>
          {options.map((o) => {
            const on = selected.includes(o.id);
            return (
              <button
                type="button"
                key={o.id}
                onClick={() => toggle(o.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 8px",
                  borderRadius: 5,
                  cursor: "pointer",
                  transition: "background .1s",
                  background: on ? C.blueTint1 : "transparent",
                  border: "none",
                  width: "100%",
                  textAlign: "left",
                }}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: `1.5px solid ${on ? C.blue : C.line}`,
                    background: on ? C.blue : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all .12s",
                  }}
                >
                  {on && (
                    <svg width="11" height="11" viewBox="0 0 12 12" aria-hidden="true">
                      <path
                        d="M3 6l2 2 4-4"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  )}
                </span>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: on ? 600 : 500,
                      color: C.navy,
                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                    }}
                  >
                    {o.name}
                  </div>
                  <div style={{ fontSize: 10.5, color: C.slateMut, marginTop: 1 }}>{o.code}</div>
                </div>
                <SBadge s={o.status} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* AssignPicker — verbatim port of designv2/src/cells.jsx AssignPicker.
   '?' avatar opens list of people for unassigned rows. Popover is portal-mounted
   (position:fixed via createPortal) so it floats above sticky/scrollable tables. */
export const AssignPicker: FC<{
  people: Person[];
  excludePids?: number[];
  onPick: (p: Person) => void;
  size?: number;
  label?: string;
}> = ({ people, excludePids = [], onPick, size = 24, label = "Unassigned" }) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const computePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popH = 320;
    const popW = 260;
    let top = r.bottom + 4;
    if (top + popH > window.innerHeight - 10) top = Math.max(8, r.top - popH - 4);
    let left = r.left;
    if (left + popW > window.innerWidth - 10) left = window.innerWidth - popW - 10;
    setPos({ top, left });
  }, []);

  useEffect(() => {
    function h(e: MouseEvent) {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        btnRef.current &&
        !btnRef.current.contains(target)
      )
        setOpen(false);
    }
    function scroll() {
      if (open) computePos();
    }
    document.addEventListener("mousedown", h);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", scroll);
    return () => {
      document.removeEventListener("mousedown", h);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", scroll);
    };
  }, [open, computePos]);

  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  function toggle() {
    if (!open) computePos();
    setOpen(!open);
  }

  const filtered = people.filter((p) => {
    if (p.status !== "active") return false;
    if (p.excludeFromPlanning) return false;
    if (excludePids.includes(p.id)) return false;
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const popover = open
    ? createPortal(
        <div
          ref={ref}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            background: C.surface,
            borderRadius: 10,
            border: `1px solid ${C.line}`,
            boxShadow: C.shL,
            zIndex: 9999,
            width: 280,
            maxHeight: 340,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${C.lineSoft}` }}>
            <input
              ref={searchRef}
              aria-label="Search people"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search people…"
              style={{
                width: "100%",
                padding: "5px 8px",
                borderRadius: 5,
                border: `1px solid ${C.line}`,
                fontSize: 12,
                outline: "none",
                color: C.navy,
                background: C.surface,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            />
          </div>
          <div style={{ overflow: "auto", padding: 4, flex: 1 }}>
            {filtered.length === 0 && (
              <div
                style={{
                  padding: "18px 12px",
                  fontSize: 11.5,
                  color: C.slateMut,
                  textAlign: "center",
                  lineHeight: 1.45,
                }}
              >
                No matches
              </div>
            )}
            {filtered.map((p) => (
              <button
                type="button"
                key={p.id}
                onClick={() => {
                  onPick(p);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 5,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "background .1s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.blueTint1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Av name={p.name} sz={22} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.navyDeep,
                      fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                    }}
                  >
                    {p.name}
                  </div>
                  <div
                    style={{
                      fontSize: 10.5,
                      color: C.slateMut,
                      marginTop: 1,
                      display: "flex",
                      gap: 4,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {p.roles.slice(0, 3).map((r) => (
                      <span
                        key={r}
                        style={{
                          fontSize: 9.5,
                          padding: "1px 5px",
                          borderRadius: 3,
                          background: C.canvas,
                          color: C.slate,
                          fontWeight: 500,
                          border: `1px solid ${C.line}`,
                        }}
                      >
                        {r}
                      </span>
                    ))}
                    <span style={{ color: C.slateDim }}>· {p.wl}%</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <button
        type="button"
        ref={btnRef}
        onClick={toggle}
        title="Assign person"
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: open ? C.blue : C.rBg,
          border: `1.5px ${open ? "solid" : "dashed"} ${open ? C.blue : C.rBd}`,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: open ? "#fff" : C.r,
          fontSize: size * 0.45,
          fontWeight: 700,
          cursor: "pointer",
          transition: "all .12s",
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          padding: 0,
          lineHeight: 1,
        }}
      >
        ?
      </button>
      <button
        type="button"
        onClick={toggle}
        style={{
          color: open ? C.blue : C.r,
          fontWeight: 600,
          fontSize: 12.5,
          fontStyle: open ? "normal" : "italic",
          cursor: "pointer",
          background: "transparent",
          border: 0,
          padding: 0,
        }}
      >
        {open ? "Pick a person…" : label}
      </button>
      {popover}
    </span>
  );
};

/**
 * TagEditor — verbatim port of designv2/src/cells.jsx.
 *
 * API surface: { value, onChange, suggestions, readOnly?, placeholder?, size? }.
 * Add via Enter or comma, Backspace removes the last tag when input is empty,
 * suggestions render in a popover and are click-to-add. Case-insensitive
 * dedup. The "+ <draft>" creator row appears when the draft is non-empty
 * and not already in suggestions.
 */
export interface TagEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  suggestions?: string[];
  readOnly?: boolean;
  placeholder?: string;
  size?: "xs" | "sm";
}

export const TagEditor: FC<TagEditorProps> = ({
  value = [],
  onChange,
  suggestions = [],
  readOnly = false,
  placeholder = "+ add",
  size = "sm",
}) => {
  const { C } = useC();
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 200,
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  // Portal-mounted popover with flip-above logic. The original was an
  // absolutely-positioned child that got clipped by an ancestor's
  // overflow / by the viewport bottom when used on the last row of a
  // long table. Now anchored to the wrap's bounding rect at fixed
  // coordinates; re-computed on open / scroll / resize.
  const computePos = useCallback(() => {
    if (!wrapRef.current) return;
    const r = wrapRef.current.getBoundingClientRect();
    const popH = 240;
    const width = Math.max(200, Math.min(r.width, 320));
    let top = r.bottom + 4;
    if (top + popH > window.innerHeight - 10) {
      top = Math.max(8, r.top - popH - 4);
    }
    let left = r.left;
    if (left + width > window.innerWidth - 10) {
      left = window.innerWidth - width - 10;
    }
    setPos({ top, left, width });
  }, []);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        wrapRef.current &&
        !wrapRef.current.contains(target) &&
        popRef.current &&
        !popRef.current.contains(target)
      ) {
        setFocused(false);
      } else if (wrapRef.current && !wrapRef.current.contains(target) && !popRef.current) {
        setFocused(false);
      }
    }
    function onScroll() {
      if (focused) computePos();
    }
    document.addEventListener("mousedown", onClick);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onClick);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [focused, computePos]);

  useEffect(() => {
    if (focused) computePos();
  }, [focused, computePos]);

  function remove(i: number) {
    if (readOnly) return;
    onChange(value.filter((_, idx) => idx !== i));
  }
  function add(tag: string) {
    const t = (tag || "").trim();
    if (!t) return;
    if (value.some((v) => v.toLowerCase() === t.toLowerCase())) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
    setHi(0);
  }

  const q = draft.trim().toLowerCase();
  const sugg = suggestions
    .filter(
      (s) =>
        !value.some((v) => v.toLowerCase() === s.toLowerCase()) &&
        (!q || s.toLowerCase().includes(q)),
    )
    .slice(0, 8);

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const target = sugg[hi];
      if (target && draft) add(target);
      else if (draft) add(draft);
    } else if (e.key === "Backspace" && !draft && value.length) {
      remove(value.length - 1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((cur) => Math.min(cur + 1, Math.max(0, sugg.length - 1)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((cur) => Math.max(0, cur - 1));
    } else if (e.key === "Escape") {
      setDraft("");
      setFocused(false);
      inputRef.current?.blur();
    }
  }

  const chipPad = size === "xs" ? "2px 6px" : "3px 8px";
  const chipFs = size === "xs" ? 10.5 : 11;

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
        alignItems: "center",
        minHeight: 24,
      }}
    >
      {value.length === 0 && !focused && !readOnly && (
        <button
          type="button"
          onClick={() => {
            setFocused(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          style={{
            fontSize: 11,
            color: C.slateMut,
            fontStyle: "italic",
            cursor: "text",
            padding: "2px 4px",
            background: "transparent",
            border: 0,
          }}
        >
          {placeholder}
        </button>
      )}
      {value.length === 0 && readOnly && (
        <span style={{ fontSize: 11, color: C.slateDim, fontStyle: "italic" }}>—</span>
      )}
      {value.map((tag, i) => {
        const tone = roleTone(tag);
        const bg =
          tone === "blue"
            ? C.blueTint2
            : tone === "navy"
              ? C.blueTint2
              : tone === "y"
                ? C.yBg
                : tone === "g"
                  ? C.gBg
                  : tone === "pink"
                    ? C.blueTint1
                    : C.canvas;
        const co =
          tone === "blue"
            ? C.blueDeep
            : tone === "navy"
              ? C.navy
              : tone === "y"
                ? C.y
                : tone === "g"
                  ? C.g
                  : tone === "pink"
                    ? C.navy
                    : C.slate;
        const bd =
          tone === "blue" ? C.blueTint4 : tone === "y" ? C.yBd : tone === "g" ? C.gBd : C.line;
        const chipStyle: CSSProperties = {
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: chipPad,
          borderRadius: 10,
          background: bg,
          border: `1px solid ${bd}`,
          fontSize: chipFs,
          fontWeight: 600,
          color: co,
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          whiteSpace: "nowrap",
        };
        return (
          <span key={tag} style={chipStyle}>
            {tag}
            {!readOnly && (
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${tag}`}
                title="Remove"
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: "50%",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  color: co,
                  opacity: 0.6,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  fontSize: 11,
                  fontWeight: 700,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "1";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "0.6";
                }}
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      {!readOnly && (focused || value.length > 0) && (
        <input
          ref={inputRef}
          aria-label={value.length === 0 ? placeholder : "Add tag"}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setHi(0);
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            if (draft) add(draft);
          }}
          onKeyDown={onKey}
          placeholder={value.length === 0 ? placeholder : "+"}
          style={{
            border: "none",
            outline: "none",
            background: "transparent",
            fontSize: chipFs,
            color: C.navy,
            minWidth: value.length === 0 ? 60 : 32,
            width: Math.max(32, draft.length * 7 + 16),
            padding: "2px 2px",
            fontFamily: "'Source Sans Pro',system-ui,sans-serif",
          }}
        />
      )}
      {focused &&
        sugg.length > 0 &&
        !readOnly &&
        createPortal(
          <div
            ref={popRef}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 8,
              boxShadow: C.shL,
              width: pos.width,
              zIndex: 9999,
              padding: 4,
              maxHeight: 220,
              overflow: "auto",
            }}
          >
            <div
              style={{
                fontSize: 9.5,
                color: C.slateMut,
                padding: "3px 8px 5px",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                fontWeight: 700,
              }}
            >
              Suggestions
            </div>
            {sugg.map((s, i) => (
              <button
                key={s}
                type="button"
                aria-label={`Add ${s}`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(s);
                }}
                onMouseEnter={() => setHi(i)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "5px 8px",
                  borderRadius: 4,
                  fontSize: 11.5,
                  color: C.navy,
                  cursor: "pointer",
                  background: hi === i ? C.blueTint1 : "transparent",
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  border: 0,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path d="M5 1v8M1 5h8" stroke={C.blue} strokeWidth="1.4" strokeLinecap="round" />
                </svg>
                {s}
              </button>
            ))}
            {draft && !sugg.some((s) => s.toLowerCase() === draft.trim().toLowerCase()) && (
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(draft);
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "5px 8px",
                  borderRadius: 4,
                  fontSize: 11.5,
                  color: C.slateMut,
                  cursor: "pointer",
                  borderTop: `1px dashed ${C.lineSoft}`,
                  marginTop: 4,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  background: "transparent",
                  border: 0,
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path
                    d="M5 1v8M1 5h8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
                Create “<strong style={{ color: C.navy }}>{draft}</strong>”
              </button>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
};

/* RolePicker — verbatim port of designv2/src/cells.jsx RolePicker.
   Inline cell editor for a member row's role. Renders a button showing current
   role; click opens a portal-mounted popover with declared (project-starred)
   roles first, catalogue next, then a free-type "create" affordance. */
export const RolePicker: FC<{
  value: string;
  onChange: (next: string) => void;
  declared?: string[];
  catalogue?: string[];
}> = ({ value, onChange, declared = [], catalogue = [] }) => {
  const { C } = useC();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const computePos = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const popH = 300;
    const popW = 240;
    let top = r.bottom + 4;
    if (top + popH > window.innerHeight - 10) top = Math.max(8, r.top - popH - 4);
    let left = r.left;
    if (left + popW > window.innerWidth - 10) left = window.innerWidth - popW - 10;
    setPos({ top, left });
  }, []);
  useEffect(() => {
    function h(e: MouseEvent) {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        btnRef.current &&
        !btnRef.current.contains(target)
      )
        setOpen(false);
    }
    function scroll() {
      if (open) computePos();
    }
    document.addEventListener("mousedown", h);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", scroll);
    return () => {
      document.removeEventListener("mousedown", h);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", scroll);
    };
  }, [open, computePos]);

  const searchRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  const decSet = new Set(declared);
  const catSet = new Set(catalogue);
  const merged: Array<{ r: string; src: "declared" | "catalogue" | "custom" }> = [];
  for (const r of declared) merged.push({ r, src: "declared" });
  for (const r of catalogue) {
    if (!decSet.has(r)) merged.push({ r, src: "catalogue" });
  }
  if (value && !decSet.has(value) && !catSet.has(value)) merged.push({ r: value, src: "custom" });
  const qlc = q.trim().toLowerCase();
  const shown = qlc ? merged.filter((m) => m.r.toLowerCase().includes(qlc)) : merged;
  const canCreate = qlc && !merged.some((m) => m.r.toLowerCase() === qlc);

  function pick(r: string) {
    onChange(r);
    setOpen(false);
    setQ("");
  }

  const tone = roleTone(value);

  const popover = open
    ? createPortal(
        <div
          ref={ref}
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            background: C.surface,
            borderRadius: 10,
            border: `1px solid ${C.line}`,
            boxShadow: C.shL,
            zIndex: 9999,
            width: 240,
            maxHeight: 320,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ padding: 8, borderBottom: `1px solid ${C.lineSoft}` }}>
            <input
              ref={searchRef}
              aria-label="Search roles"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (shown[0]) pick(shown[0].r);
                  else if (canCreate) pick(q.trim());
                }
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder="Search or type a new role…"
              style={{
                width: "100%",
                padding: "5px 8px",
                borderRadius: 5,
                border: `1px solid ${C.line}`,
                fontSize: 12,
                outline: "none",
                color: C.navy,
                background: C.surface,
                fontFamily: "'Source Sans Pro',system-ui,sans-serif",
              }}
            />
          </div>
          <div style={{ overflow: "auto", padding: 4, flex: 1 }}>
            {shown.length === 0 && !canCreate && (
              <div
                style={{
                  padding: "18px 12px",
                  fontSize: 11.5,
                  color: C.slateMut,
                  textAlign: "center",
                }}
              >
                No matches
              </div>
            )}
            {shown.map(({ r, src }) => {
              const sel = r === value;
              return (
                <button
                  type="button"
                  key={r}
                  onClick={() => pick(r)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "5px 8px",
                    borderRadius: 5,
                    background: sel ? C.blueTint1 : "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                  }}
                  onMouseEnter={(e) => {
                    if (!sel) e.currentTarget.style.background = C.surfaceAlt;
                  }}
                  onMouseLeave={(e) => {
                    if (!sel) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <Badge tone={roleTone(r) as never}>{r}</Badge>
                  {src === "declared" && (
                    <span
                      title="Declared on this project"
                      style={{
                        marginLeft: "auto",
                        fontSize: 9.5,
                        color: C.blueDeep,
                        fontWeight: 700,
                        letterSpacing: 0.3,
                        textTransform: "uppercase",
                      }}
                    >
                      project
                    </span>
                  )}
                  {src === "custom" && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: 9.5,
                        color: C.slateMut,
                        fontStyle: "italic",
                      }}
                    >
                      custom
                    </span>
                  )}
                </button>
              );
            })}
            {canCreate && (
              <button
                type="button"
                onClick={() => pick(q.trim())}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  width: "100%",
                  padding: "6px 8px",
                  borderRadius: 5,
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  textAlign: "left",
                  color: C.slateMut,
                  fontSize: 11.5,
                  borderTop: `1px dashed ${C.lineSoft}`,
                  marginTop: 4,
                  fontFamily: "'Source Sans Pro',system-ui,sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = C.blueTint1;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
                  <path
                    d="M5 1v8M1 5h8"
                    stroke="currentColor"
                    strokeWidth="1.4"
                    strokeLinecap="round"
                  />
                </svg>
                Use “<strong style={{ color: C.navy }}>{q.trim()}</strong>”
              </button>
            )}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={() => {
          if (!open) computePos();
          setOpen(!open);
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          background: open ? C.blueTint1 : "transparent",
          border: `1px dashed ${open ? C.blueTint4 : value ? "transparent" : C.line}`,
          padding: value ? "1px 4px" : "3px 8px",
          borderRadius: 6,
          cursor: "pointer",
          fontSize: 11,
          color: C.slateMut,
          fontFamily: "'Source Sans Pro',system-ui,sans-serif",
        }}
      >
        {value ? (
          <Badge tone={tone as never}>{value}</Badge>
        ) : (
          <span style={{ color: C.slateMut, fontStyle: "italic" }}>Pick a role…</span>
        )}
        <svg width="8" height="8" viewBox="0 0 10 10" aria-hidden="true" style={{ opacity: 0.6 }}>
          <path
            d="M2 3.5l3 3 3-3"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
          />
        </svg>
      </button>
      {popover}
    </>
  );
};
